/**
 * PATH: packages/bills/src/hooks.ts
 * FILE_ID: BILLS-PKG-HOOKS-001
 * PURPOSE: Tiny transport-agnostic React hooks the workspace UI uses.
 *
 * WHY: Apps already wrap @quotech/bills with their own caching strategy
 *      (apps/billing → TanStack Query; apps/sam → vanilla useState).
 *      The hooks here are deliberately small (just the workspace's needs)
 *      and rely on the injected `BillsClient` for transport — no
 *      external state library imported here.
 *
 * RACE PROTECTION: list/detail use a sequence number ("token") so a
 *      faster newer request can't be overwritten by a slower older one.
 *      AbortController would be cleaner but the BillsClient interface
 *      stays minimal (no signal param) — apps that already abort at
 *      transport level still benefit.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useBillsClient } from './client';
import type {
  BillActionOptions,
  BillDetail,
  BillDuplicateInfo,
  BillEditablePatch,
  BillFlag,
  BillListOrder,
  BillListRow,
  BillsListResponse,
  BillStatusFilter,
  MarkBillPaidInput,
  SetSupplierTermInput,
} from './types';

/** Rows fetched per infinite-scroll page. */
const DEFAULT_PAGE_SIZE = 50;

interface AsyncResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * GET bills list. Refetches when `status` changes.
 *
 * `refetchIntervalMs` polls the backend so newly-arrived / freshly-
 * extracted bills appear without a manual Refresh click. The poll
 * pauses while the document is hidden (visibilitychange) so we don't
 * hammer the API for backgrounded tabs. Set to 0 to disable.
 */
export function useBillsList(
  filter: {
    status?: BillStatusFilter;
    q?: string;
    /** Attention-reason filter (queue redesign) — composes with status. */
    flag?: BillFlag;
    /** ``triage`` = overdue-first operator order; omit for newest-first. */
    order?: BillListOrder;
  } = {},
  refetchIntervalMs: number = 0,
  pageSize: number = DEFAULT_PAGE_SIZE,
): AsyncResult<BillsListResponse> & {
  /** Fetch + append the next page. No-op when already loading or no more. */
  loadMore: () => void;
  /** True while a `loadMore` append is in flight (distinct from `loading`). */
  loadingMore: boolean;
  /** True when the backend reports another page exists for this filter. */
  hasMore: boolean;
} {
  const client = useBillsClient();
  const status = filter.status;
  const flag = filter.flag;
  const order = filter.order;
  // Normalise blank/whitespace search to undefined so it's dropped from the
  // query (and doesn't thrash the fetch effect on every keystroke of spaces).
  const q = filter.q?.trim() ? filter.q.trim() : undefined;
  const [data, setData] = useState<BillsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tokenRef = useRef(0);
  // Mirror of the accumulated rows so `loadMore` knows the offset and the
  // poll knows how wide a window to refresh — without adding them to the
  // fetch effect's deps (which would re-fetch the first page on every append).
  const rowsRef = useRef<BillListRow[]>([]);

  // Fetch the FIRST page (a full reset). Used for the initial load, status/q
  // filter changes, and manual Refresh — always exactly `pageSize` rows at
  // offset 0, dropping any scrolled-in pages (that's what a reset means).
  const fetchFirst = useCallback(async () => {
    const token = ++tokenRef.current;
    setLoading(true);
    setError(null);
    try {
      const resp = await client.list({ status, q, flag, order, limit: pageSize, offset: 0 });
      if (token !== tokenRef.current) return;
      rowsRef.current = resp.bills;
      setData(resp);
    } catch (e) {
      if (token !== tokenRef.current) return;
      setError(toMessage(e, 'Failed to load bills'));
    } finally {
      if (token === tokenRef.current) setLoading(false);
    }
  }, [client, status, q, flag, order, pageSize]);

  // Lightweight background poll. New bills always arrive newest-first at the
  // TOP of the list, so a poll only needs the FIRST page (`pageSize` rows at
  // offset 0) to surface arrivals + keep the head fresh. This replaces the old
  // "refresh the whole loaded window" poll that requested up to 200 rows every
  // interval — cutting per-tab backend load from ~200 rows/30s to pageSize/30s.
  //
  // The fresh first page is MERGED over the existing rows (not clobbered): the
  // operator's scrolled-in pages below stay put, de-duped by id so rows that
  // shifted down when new bills arrived aren't repeated. `page.has_more` is
  // preserved from the loaded window — a first-page poll can't tell whether
  // pages beyond that window still exist.
  const pollRefresh = useCallback(async () => {
    const token = ++tokenRef.current;
    try {
      const resp = await client.list({ status, q, flag, order, limit: pageSize, offset: 0 });
      if (token !== tokenRef.current) return;
      const freshIds = new Set(resp.bills.map((b) => b.id));
      const merged = [
        ...resp.bills,
        ...rowsRef.current.filter((b) => !freshIds.has(b.id)),
      ];
      rowsRef.current = merged;
      setData((prev) => ({
        ...resp,
        bills: merged,
        // Keep the loaded window's page envelope (esp. `has_more`) — a
        // first-page poll can't know whether pages beyond it still exist.
        page: prev?.page ?? resp.page,
      }));
    } catch {
      // Silent poll: on failure leave the loaded rows + list-level error
      // untouched. The next tick (or a manual Refresh) will retry.
    }
  }, [client, status, q, flag, order, pageSize]);

  const loadMore = useCallback(() => {
    if (loadingMore) return;
    if (!data?.page?.has_more) return;
    // Capture the token WITHOUT bumping — an append belongs to the current
    // view. If a reset (status/q change or poll) bumps the token mid-flight,
    // we discard the stale append instead of corrupting the row list.
    const token = tokenRef.current;
    const offset = rowsRef.current.length;
    setLoadingMore(true);
    void (async () => {
      try {
        const resp = await client.list({ status, q, flag, order, limit: pageSize, offset });
        if (token !== tokenRef.current) return;
        const merged = [...rowsRef.current, ...resp.bills];
        rowsRef.current = merged;
        setData({ ...resp, bills: merged });
      } catch {
        // A failed page-append leaves the loaded rows intact; the sentinel
        // will retry on the next scroll. Don't clobber the list-level error.
      } finally {
        if (token === tokenRef.current) setLoadingMore(false);
      }
    })();
  }, [client, status, q, flag, order, pageSize, data, loadingMore]);

  useEffect(() => {
    fetchFirst();
  }, [fetchFirst]);

  // Polling — separate effect so changing the interval doesn't
  // trigger an immediate refetch (the fetch effect above handles
  // that). Pauses when the tab is hidden to be a good citizen on
  // backgrounded pages. Uses the lightweight first-page merge so a poll
  // never flashes skeletons, never re-requests the whole loaded window,
  // and never drops the operator's scrolled-in pages.
  useEffect(() => {
    if (!refetchIntervalMs || refetchIntervalMs <= 0) return;
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer) return;
      timer = setInterval(() => {
        if (typeof document === 'undefined' || !document.hidden) {
          pollRefresh();
        }
      }, refetchIntervalMs);
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVis = () => {
      if (typeof document !== 'undefined' && document.hidden) stop();
      else start();
    };
    start();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVis);
    }
    return () => {
      stop();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVis);
      }
    };
  }, [pollRefresh, refetchIntervalMs]);

  return {
    data,
    loading,
    error,
    // Manual Refresh is still a FULL first-page reset (unchanged behaviour).
    refetch: fetchFirst,
    loadMore,
    loadingMore,
    hasMore: !!data?.page?.has_more,
  };
}

/** GET single bill. Refetches when `billId` changes. */
export function useBillDetail(billId: string | null): AsyncResult<BillDetail> & {
  setData: (b: BillDetail | null) => void;
} {
  const client = useBillsClient();
  const [data, setData] = useState<BillDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(!!billId);
  const [error, setError] = useState<string | null>(null);
  const tokenRef = useRef(0);

  const refetch = useCallback(async () => {
    if (!billId) {
      setData(null);
      setLoading(false);
      return;
    }
    const token = ++tokenRef.current;
    setLoading(true);
    setError(null);
    try {
      const resp = await client.detail(billId);
      if (token !== tokenRef.current) return;
      setData(resp);
    } catch (e) {
      if (token !== tokenRef.current) return;
      setError(toMessage(e, 'Failed to load bill'));
    } finally {
      if (token === tokenRef.current) setLoading(false);
    }
  }, [client, billId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, loading, error, refetch, setData };
}

interface MutationState {
  busy: boolean;
  error: string | null;
}

/**
 * Extract the ``duplicate`` payload from a money-action error when the
 * backend blocked it with 409 ``code=possible_duplicate`` (the
 * duplicate-bill backstop on approve / mark-paid / push-to-xero).
 * Handles both transports: QuotechApiError (code + details on the
 * error itself) and axios (code + payload under ``response.data``).
 * Returns null for every other error.
 */
export function getBillDuplicateConflict(e: unknown): BillDuplicateInfo | null {
  if (!e || typeof e !== 'object') return null;
  const err = e as {
    code?: unknown;
    details?: { duplicate?: BillDuplicateInfo };
    response?: { data?: { code?: unknown; duplicate?: BillDuplicateInfo } };
  };
  if (err.code === 'possible_duplicate' && err.details?.duplicate) {
    return err.details.duplicate;
  }
  if (
    err.response?.data?.code === 'possible_duplicate'
    && err.response.data.duplicate
  ) {
    return err.response.data.duplicate;
  }
  return null;
}

/** Approve / reject / push-to-xero / link-job / re-extract / reclassify actions on a bill. */
export function useBillActions(): {
  approve: (billId: string, opts?: BillActionOptions) => Promise<BillDetail | null>;
  reject: (billId: string, reason?: string) => Promise<BillDetail | null>;
  pushToXero: (billId: string, opts?: BillActionOptions) => Promise<BillDetail | null>;
  linkJob: (billId: string, jobId: number | null) => Promise<BillDetail | null>;
  /**
   * Restore a cascade fail-closed bill back to its pre_quarantine_status.
   * Returns the updated BillDetail, or null on failure / when the client
   * hasn't wired `restore` (apps/billing).
   */
  restore: (billId: string) => Promise<BillDetail | null>;
  update: (billId: string, patch: BillEditablePatch) => Promise<BillDetail | null>;
  /**
   * Set the linked supplier's payment term and re-derive this bill's due
   * date. Returns the updated BillDetail, or null on failure (state.error
   * carries the message). No-op returning null when the client hasn't wired
   * `setSupplierTerm` (older embedders).
   */
  setSupplierTerm: (
    billId: string,
    input: SetSupplierTermInput,
  ) => Promise<BillDetail | null>;
  /**
   * Mark the bill as paid (or clear it). Returns the updated BillDetail, or
   * null on failure / when the client hasn't wired `markPaid`.
   */
  markPaid: (billId: string, input: MarkBillPaidInput) => Promise<BillDetail | null>;
  /**
   * Re-run the AI extraction on a bill's source email. Use this when
   * the initial extraction came back empty (e.g. the LLM failed to map
   * a statement onto the invoice schema). Returns the updated
   * BillDetail with freshly populated dates / totals / line items, or
   * null on failure.
   */
  reExtract: (emailId: string) => Promise<BillDetail | null>;
  /**
   * Reclassify the email backing a bill — typical use is "this isn't
   * a bill, it's our outgoing invoice". Returns true on success; the
   * caller should then refetch the bills list (the row will have been
   * swept by BillListView._cleanup_orphaned_pending on the next GET).
   * Returns false on failure (state.error carries the message).
   */
  setEmailCategory: (emailId: string, category: string) => Promise<boolean>;
  state: MutationState;
} {
  const client = useBillsClient();
  const [state, setState] = useState<MutationState>({ busy: false, error: null });

  const wrap = useCallback(
    async <T,>(label: string, fn: () => Promise<T>): Promise<T | null> => {
      setState({ busy: true, error: null });
      try {
        const result = await fn();
        setState({ busy: false, error: null });
        return result;
      } catch (e) {
        // 409 possible_duplicate is a QUESTION, not a failure — rethrow
        // so the caller can show the confirm dialog and re-submit with
        // force. Everything else keeps the swallow-into-state contract.
        if (getBillDuplicateConflict(e)) {
          setState({ busy: false, error: null });
          throw e;
        }
        setState({ busy: false, error: toMessage(e, label + ' failed') });
        return null;
      }
    },
    [],
  );

  return {
    approve: (billId, opts) => wrap('Approve', () => client.approve(billId, opts)),
    reject: (billId, reason) => wrap('Reject', () => client.reject(billId, reason)),
    pushToXero: (billId, opts) => wrap('Push to Xero', () => client.pushToXero(billId, opts)),
    linkJob: (billId, jobId) => wrap('Link job', () => client.linkJob(billId, jobId)),
    restore: (billId) =>
      wrap('Restore', () =>
        client.restore ? client.restore(billId) : Promise.resolve(null),
      ),
    update: (billId, patch) => wrap('Update', () => client.update(billId, patch)),
    setSupplierTerm: (billId, input) =>
      wrap('Set payment term', () =>
        client.setSupplierTerm
          ? client.setSupplierTerm(billId, input)
          : Promise.resolve(null),
      ),
    markPaid: (billId, input) =>
      wrap('Update payment', () =>
        client.markPaid ? client.markPaid(billId, input) : Promise.resolve(null),
      ),
    reExtract: (emailId) => wrap('Re-extract', () => client.extractFromEmail(emailId)),
    setEmailCategory: async (emailId, category) => {
      const result = await wrap('Reclassify', async () => {
        await client.setEmailCategory(emailId, category);
        return true as const;
      });
      return result === true;
    },
    state,
  };
}

function toMessage(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}
